import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FormComponentLookup, FormEntryComponentClass } from '@ptl/forms';
import { ContentStorageService } from '@ptl/global-components';
import { NormalizedResponse } from '@ptl/xhr';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { OnExchangeEligibilityStateStructure, ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME } from 'src/app/angular-elements/on-exchange-enrollment/reducers/eligibility.reducer';
import { EligibilityManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/eligibility-manager.service';
import { EnrollManagerService } from 'src/app/angular-elements/on-exchange-enrollment/services/enroll-manager.service';
import { PayByPhoneService } from 'src/app/angular-elements/on-exchange-enrollment/services/pay-by-phone.service';
import { MarketplacePaymentOptionsControlsAsJson, OnExchangeEnrollmentPaymentFormStateStructure, ON_EXCHANGE_PAYMENT_METHODS } from 'src/app/angular-elements/shared/interfaces/on-exchange-enrollment-payment-form.interface';
import { ON_EXCHANGE_ENROLLMENT_PAYMENT_FORM_REDUCER_NAME } from 'src/app/shared/reducers/on-exchange-enrollment-payment-form.reducer';
import { PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT, PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT_KEYS } from '../../configs/content-storage.enum';
import { PaymentManagerService } from '../../services/payment-manager.service';

enum PAYMENT_DESCRIPTIONS {
  CREDIT_CARD = `One month's premium will be charged to your credit/debit card when we receive and process your application.<br/><strong>Please note that coverage does not begin until your effective date.</strong><br/>For future payments, you will receive a statement each month that will contain options for continued payments.`,
  BANK_ACCOUNT = `One month's premium will be charged to your bank account when we receive and process your application.<br/><strong>Please note that coverage does not begin until your effective date.</strong><br/>For future payments, you will receive a statement each month that will contain options for continued payments.`,
  BILL_ME_LATER = `A bill will be sent to you for the amount of your first month's premium, payable via paper check, cashier's check, or money order. If you select SEND ME A BILL, you must pay the full amount of your bill. Your coverage will not begin unless we receive a payment in full by the date listed on your bill. Please note that coverage does not begin until the effective date listed on your bill.`,
  PAY_BY_PHONE = `Before transferring your customer into the Payment IVR you must click the "Next" button to ensure the premium amount is sent back to salesforce.com`
}
interface MarketplacePaymentOptionsFormControls {
  selectedPaymentType: AbstractControl;
}
@FormComponentLookup('marketplace-payment-options')
@Component({
    selector: 'app-marketplace-payment-options',
    templateUrl: './marketplace-payment-options.component.html',
    styleUrls: ['./marketplace-payment-options.component.scss'],
    standalone: false
})
export class MarketplacePaymentOptionsComponent extends FormEntryComponentClass implements OnInit, OnDestroy {

  marketplacePaymentOptionsForm: UntypedFormGroup;
  marketplacePaymentOptionsControlsAsJsonFormChangesSubscription: Subscription;

  /** House temporary CMS redirect "tutorial" images to get to payment */
  imageUrls: {
    login: string;
    continue: string;
    appSelection: string;
    premiumPayment: string;
  } = {
    login: null,
    continue: null,
    appSelection: null,
    premiumPayment: null
  };

  contentStorageSubscription: Subscription;

  paymentOptions = [];

  constructor(
    private paymentManagerService: PaymentManagerService,
    private eligibilityManagerService: EligibilityManagerService,
    private enrollManagerService: EnrollManagerService,
    private fb: UntypedFormBuilder,
    private payByPhoneService: PayByPhoneService,
    private contentStorage: ContentStorageService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {

    // this.paymentOptions.push({
    //   label: 'PAY DIRECTLY FROM MY BANK ACCOUNT',
    //   value: ON_EXCHANGE_PAYMENT_METHODS.BANK_ACCOUNT,
    //   description: PAYMENT_DESCRIPTIONS.BANK_ACCOUNT
    // });
    this.paymentOptions.push({
      label: 'CHARGE MY CREDIT / DEBIT CARD (Including Pre-Paid Debit Cards)',
      value: ON_EXCHANGE_PAYMENT_METHODS.CREDIT_CARD,
      description: PAYMENT_DESCRIPTIONS.CREDIT_CARD
    });
    this.paymentOptions.push({
      label: 'SEND ME A BILL',
      value: ON_EXCHANGE_PAYMENT_METHODS.BILL_ME_LATER,
      description: PAYMENT_DESCRIPTIONS.BILL_ME_LATER
    });
    // this.paymentOptions.push({{
    //   label: 'PAY BY PHONE',
    //   value: ON_EXCHANGE_PAYMENT_METHODS.PAY_BY_PHONE,
    //   description: PAYMENT_DESCRIPTIONS.PAY_BY_PHONE
    // });

    const eligibilityState: OnExchangeEligibilityStateStructure
      = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId );

    if (matchedProspects.length > 0) {
      const prospect = matchedProspects[0];
      await this.eligibilityManagerService.get(prospect.prospectId);

      this.marketplacePaymentOptionsForm = this.fb.group({
        selectedPaymentType: this.fb.control(this.getValFromState(), [Validators.required])
      });

      /** Hide form-driven next button, we'll make our own */
      const hideNextButtonEvent = {
        formControlName: null,
        type: 'hideNextButton',
        value: null
      };
      this.formStreamTrigger.triggerFormStream(hideNextButtonEvent);

      /** Hide form-driven back button, we'll make our own */
      const hideBackButtonEvent = {
        formControlName: null,
        type: 'hideBackButton',
        value: null
      };
      this.formStreamTrigger.triggerFormStream(hideBackButtonEvent);

      this.setupSubscription();

    }

    this.contentStorageSubscription = this.contentStorage.contentChanged$.subscribe(() => {
      if (this.contentStorage.getContentMap(PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT)) {
        const content = this.contentStorage.getContentMap(PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT) as Map<any, any>;
        this.imageUrls = {
          login: content.get(PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT_KEYS.LOGIN_IMAGE) || null,
          continue: content.get(PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT_KEYS.CONTINUE_IMAGE) || null,
          appSelection: content.get(PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT_KEYS.APP_SELECTION_IMAGE) || null,
          premiumPayment: content.get(PAGE_INDIVIDUAL_ON_EXCHANGE_PLAN_ENROLLMENT_PAYMENT_KEYS.PREMIUM_PAYMENT_IMAGE) || null,
        }
      }
    });

  }

  ngOnDestroy(): void {
    this.contentStorageSubscription.unsubscribe();
  }

  trackByIndex(index: number): number {
    return index;
  }

  setupSubscription() {

    this.marketplacePaymentOptionsControlsAsJsonFormChangesSubscription = this.marketplacePaymentOptionsForm.valueChanges
    .pipe(debounceTime(300))
    .subscribe((v: MarketplacePaymentOptionsControlsAsJson) => {

      const dynamicObject = {};
      dynamicObject[this.entry.formControlAttributes.formControlName] = v;

      /** Update global form (trigger save to state) */
      this.form.patchValue(dynamicObject);
    });

  }

  goBack() {
    const event = {
      formControlName: null,
      type: 'triggerBackPage',
      value: null
    };
    this.formStreamTrigger.triggerFormStream(event);
  }

  getValFromState() {
    const onExchangePaymentState: OnExchangeEnrollmentPaymentFormStateStructure
    = this.reducerUtilityService.getPayloadStates()[ON_EXCHANGE_ENROLLMENT_PAYMENT_FORM_REDUCER_NAME];
    const selectedPaymentType = onExchangePaymentState.marketplacePaymentOptions.selectedPaymentType;
    return selectedPaymentType;
  }
  async submit() {
    /** Payment Options */

    const eligibilityState: OnExchangeEligibilityStateStructure
    = this.reducerUtilityService.getComponentStates()[ON_EXCHANGE_ELIGIBILITY_REDUCER_NAME];
    const matchedProspects = eligibilityState.prospects.filter(p => eligibilityState.currentProspectId === p.prospectId );

    if (matchedProspects.length > 0) {
      const prospect = matchedProspects[0];

      let individualResponsibilityAmount = null;
      if (prospect.individualResponsibilityAmount) {
        /** From view-plans/enrollment pages */
        individualResponsibilityAmount = prospect.individualResponsibilityAmount;
      } else {
        /** From application-status page, after submit ( where data is on eligibility response object ) */
        const ffmData = prospect.ffmData;
        const applicantEnrollmentResponse = ffmData?.ApplicantEnrollmentResponse;
        const activity
          = applicantEnrollmentResponse?.policyCreationActivityOrMembersMaintenanceActivityOrPolicyPremiumChangeActivity &&
            applicantEnrollmentResponse?.policyCreationActivityOrMembersMaintenanceActivityOrPolicyPremiumChangeActivity[0];
        const policyCreationActivity = activity?.PolicyCreationActivity;
        const affectedInsurancePremium = policyCreationActivity?.AffectedInsurancePremium;
        const individualResponsibilityObject = affectedInsurancePremium?.InsurancePremiumIndividualResponsibilityTotalAmount;
        individualResponsibilityAmount = individualResponsibilityObject.value;
      }
      this.paymentManagerService.createPaymentOnX(
        Number(individualResponsibilityAmount).toFixed(2),
        this.enrollManagerService.getPaymentTransactionID()
      );

      const onExchangePaymentState: OnExchangeEnrollmentPaymentFormStateStructure
        = this.reducerUtilityService.getPayloadStates()[ON_EXCHANGE_ENROLLMENT_PAYMENT_FORM_REDUCER_NAME];
      const selectedPaymentType = onExchangePaymentState.marketplacePaymentOptions.selectedPaymentType;

      if (selectedPaymentType) {
        this.enrollManagerService.setPaymentOptionOnX({ paymentOptions: selectedPaymentType });

        /** Keep big data maintained */
        prospect.data.paymentOption = selectedPaymentType;
        this.eligibilityManagerService.set(prospect.data, prospect.prospectId);

        /**
         * Users cannot pay via our portal for PHONE options,
         * the data needs to be sent to SALESFORCE to complete payment
         */
        if (selectedPaymentType === ON_EXCHANGE_PAYMENT_METHODS.PAY_BY_PHONE) {
          /** @TODO Not implemented */
          const response = await this.payByPhoneService.payByPhone();

          if (!(response instanceof HttpErrorResponse)) {

            const successObj: NormalizedResponse<{returnCode: number}> = JSON.parse(response);
            if (successObj.payload.returnCode === 200){
              const event = {
                formControlName: null,
                type: 'triggerNextPage',
                value: null
              };
              this.formStreamTrigger.triggerFormStream(event);
              // router.go('!/marketplace/payment/review');
            } else {
                let message = '';
                const errorObj = JSON.parse(JSON.parse(response).payload.returnDescription);

                errorObj.errors.forEach(error => {
                    message += error.errorMessage + '<br>';
                });

                // modal.show({
                //     modalContent: '<p id=\'bad-response-modal\'><strong>There has been a problem accessing this service.</strong></p><p class=\'margin-top\'><small class=\'font-bold\'>More Information:</small><div style=\'height:100px; overflow:auto;\'><p>' + message + '</p></div>',
                //     height: 'auto',
                //     gridSizeClass : 'grid-5'
                // });
            }
          } else {
            // const message = JSON.parse(response.response.data).baseMessage.returnCodeDescription;

            // modal.show({
            //     modalContent: '<p id=\'bad-response-modal\'><strong>There has been a problem accessing this service.</strong></p><p class=\'margin-top\'><small class=\'font-bold\'>More Information:</small><div style=\'height:100px; overflow:auto;\'><p>' + message + '</p></div>',
            //     height: 'auto',
            //     gridSizeClass : 'grid-5'
            // });
          }
        } else {
          const event = {
            formControlName: null,
            type: 'triggerNextPage',
            value: null
          };
          this.formStreamTrigger.triggerFormStream(event);
        }
      }

    }


  }

  async goToMarketplacePayment() {
    const event = {
      formControlName: null,
      type: 'payAtMarketplace',
      value: null
    };
    this.formStreamTrigger.triggerFormStream(event);
  }
}
